// import "./list-client-quotation.scss";

import { GetQuotationModel } from "@factory/model/query-model-quotation";
import { useQyGetQuotation } from "@factory/query/quotation.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { useDateTimeContext } from "../../../shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "../../../shared/utility/formats/currency-format";
import { FilterQuotation } from "../filter-quotation/filter-quotation";
import { useFilterQuotationContext } from "../filter-quotation/filter-quotation.context";

export function ListClientQuotation() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterQuotationContext();
  const { formatDate } = useDateTimeContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: quotationResponse,
    isLoading,
    isError,
    error,
  } = useQyGetQuotation(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetQuotationModel) => {
    navigate(`../../quotation/${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterQuotation />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{quotationResponse?.count}</b>
      </h5>

      <DataTable
        value={quotationResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          field="base_package_name"
          header="Base Package"
          sortable
        ></Column>
        <Column
          field="quotation_date"
          header="Quotation Date"
          sortable
          body={(item) => <p>{formatDate(item.quotation_date)}</p>}
        ></Column>
        <Column field="location_name" header="Location" sortable></Column>
        <Column
          field="start_date"
          header="Start Date"
          sortable
          body={(item) => <p>{formatDate(item.start_date)}</p>}
        ></Column>
        <Column
          field="end_date"
          header="End Date"
          sortable
          body={(item) => <p>{formatDate(item.end_date)}</p>}
        ></Column>
        <Column
          field="amount"
          header="Amount"
          sortable
          body={(item) => <p>{currencyFormat(item.amount)}</p>}
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={quotationResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListClientQuotation" className="quotation">
      <HeaderContent title={<h1>Quotation</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListClientQuotation;
